<template>
  <BaseModal :open="open" max-width="440px"
             :position="['top', 'center']" :modalStyle="['margin-top:8%']"
             @close="close">
    <div class="v2026-mac" role="dialog" aria-modal="true" aria-labelledby="v2026-mac-title">
      <div class="v2026-mac__head">
        <span id="v2026-mac-title" class="v2026-mac__title">การ์ด LINE Mini App</span>
        <button type="button" class="v2026-mac__close" aria-label="ปิด" @click="close">×</button>
      </div>

      <div class="v2026-mac__body">
        <p v-if="loadError" class="v2026-mac__error" role="alert">โหลดตัวอย่างการ์ดไม่สำเร็จ กรุณาลองใหม่</p>
        <div v-else-if="!previews" class="v2026-mac__loading">กำลังโหลด...</div>

        <template v-else>
          <!-- Pick a card: one row per service, so four options still fit without scrolling. -->
          <div class="v2026-mac__options" role="radiogroup" aria-label="เลือกการ์ดที่จะส่ง">
            <button v-for="card in cards" :key="card.key" type="button"
                    class="v2026-mac__option"
                    :class="{ 'is-selected': selectedKey === card.key }"
                    role="radio" :aria-checked="selectedKey === card.key ? 'true' : 'false'"
                    @click="pick(card)">
              <span class="v2026-mac__radio" aria-hidden="true"></span>
              <span class="v2026-mac__option-label">{{ card.preview.menuLabel }}</span>
            </button>
          </div>

          <!-- What the customer will actually receive, straight from the server copy. -->
          <div v-if="selectedCard" class="v2026-mac__card">
            <span class="v2026-mac__content">
              <span class="v2026-mac__card-title">{{ selectedCard.preview.title }}</span>
              <span v-for="(line, i) in selectedCard.preview.lines" :key="i" class="v2026-mac__line">{{ line }}</span>
              <span v-if="selectedCard.preview.note" class="v2026-mac__note">{{ selectedCard.preview.note }}</span>
            </span>
            <span class="v2026-mac__foot">
              <span class="v2026-mac__cta">{{ selectedCard.preview.button }}</span>
            </span>
          </div>
        </template>
      </div>

      <div class="v2026-mac__actions">
        <button type="button" class="v2026-mac__send"
                :disabled="!selectedKey || sending" @click="send">ส่งการ์ด</button>
      </div>
    </div>
  </BaseModal>
</template>

<script>
import axios from 'axios'
import BaseModal from '@/components/coversation/BaseModal.vue'

// Preview copy comes from chat-server (GET /messages/mini-app-cards), which reads it from the
// same helpers that build the LINE cards — fetched once per page, it never changes at runtime.
let previewCache = null

const REGISTER_KEY = 'register'
const ENDPOINTS = {
  register: '/messages/register-invite-card',
  quote_request: '/messages/quote-request-card'
}

export default {
  name: 'mini-app-card-modal-v2026',
  components: {
    BaseModal
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    selectedConversation: {
      type: Object,
      default: null
    },
    admin: {
      type: Object,
      default: null
    },
    // Customer has no linked BeNeat account → the register card (unless canQuote wins).
    canRegister: {
      type: Boolean,
      default: false
    },
    // Chat carries a Big Cleaning tag → the quote-request card, and only that one: its Mini App
    // form handles login / registration itself, so a register card alongside is redundant.
    canQuote: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      previews: previewCache,
      loadError: false,
      selectedKey: null,
      sending: false
    }
  },
  computed: {
    // One entry per selectable card. The quote card covers several services
    // (Big Cleaning, sofa/mattress, curtain/carpet, disinfection), each a row of
    // its own keyed by the server's serviceKey — which is also what `send` posts
    // back, so the menu can never name a service the endpoint does not know.
    //
    // The quote cards are offered on EVERY chat, matching the composer button,
    // which is no longer hidden either: an admin who opened this modal wants to
    // send a card, and an empty modal is a dead end. `canQuote` therefore only
    // decides the ORDER — a Big Cleaning chat leads with the quote cards, every
    // other chat leads with whatever is most likely there. The register card is
    // still conditional: it is pointless for a customer who already has a linked
    // BeNeat account.
    cards () {
      if (!this.previews) return []
      const quote = this.previews.quote_requests.map(preview => ({
        key: preview.serviceKey,
        serviceKey: preview.serviceKey,
        preview
      }))
      if (!this.canRegister) return quote
      const register = { key: REGISTER_KEY, preview: this.previews.register }
      return this.canQuote ? [...quote, register] : [register, ...quote]
    },
    selectedCard () {
      return this.cards.find(card => card.key === this.selectedKey) || null
    }
  },
  watch: {
    open (isOpen) {
      if (isOpen) this.reset()
    }
  },
  methods: {
    async reset () {
      this.selectedKey = null
      this.sending = false
      this.loadError = false
      if (!this.previews) {
        try {
          const res = await axios.get('/messages/mini-app-cards')
          if (!res.data || !res.data.register || !Array.isArray(res.data.quote_requests) || !res.data.quote_requests.length) {
            throw new Error('bad preview payload')
          }
          previewCache = res.data
          this.previews = previewCache
        } catch (e) {
          this.loadError = true
          return
        }
      }
      // Pre-pick the first row so the preview below is never empty; with one card
      // (register) that leaves the admin only ส่งการ์ด to press, as before.
      this.selectedKey = this.cards.length ? this.cards[0].key : null
    },
    pick (card) {
      this.selectedKey = card.key
    },
    close () {
      if (this.sending) return
      this.$emit('close')
    },
    async send () {
      const card = this.selectedCard
      if (!card || this.sending) return
      // Every service shares one endpoint and tells it which card to build.
      const url = card.key === REGISTER_KEY ? ENDPOINTS.register : ENDPOINTS.quote_request
      this.sending = true
      try {
        await axios.post(url, {
          conversation: this.selectedConversation,
          adminId: this.admin && this.admin.id,
          ...(card.serviceKey ? { serviceKey: card.serviceKey } : {})
        })
        this.$emit('notify', { variant: 'success', title: 'ส่งการ์ดแล้ว', message: '' })
        this.sending = false
        this.$emit('close')
      } catch (e) {
        const data = e.response && e.response.data
        this.$emit('notify', {
          variant: 'danger',
          title: 'ส่งการ์ดไม่สำเร็จ',
          message: (data && (data.error || data.message)) || 'กรุณาลองใหม่อีกครั้ง'
        })
        this.sending = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.v2026-mac {
  --v2026-mac-main: #31C1D7;
  --v2026-mac-line-green: #06C755;

  /* Header and footer pinned, card list scrolls between them.
     The height budget: BaseModal caps its dialog at 90vh and CLIPS the overflow,
     and this modal is pushed down by `margin-top:8%` — a percentage margin
     resolves against the containing block's WIDTH, so on a wide-but-short screen
     that is a large push — plus the backdrop's 16px padding top and bottom.
     Without this cap the bottom of the card, the ส่งการ์ด button, lands outside
     the viewport and cannot be tapped. */
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 8vw - 48px);
}
.v2026-mac__head {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #FFFFFF;
  border-bottom: 1px solid #EBEDF0;
  padding: 16px 24px;
}
.v2026-mac__title {
  font-size: 16px;
  font-weight: 600;
  color: #1F2937;
}
.v2026-mac__close {
  border: none;
  background: transparent;
  padding: 0;
  font-size: 22px;
  line-height: 1;
  color: #66696C;
  cursor: pointer;
}
.v2026-mac__body {
  /* min-height:0 lets this flex child shrink below its content so overflow-y
     actually scrolls instead of stretching the modal past its max-height. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 20px 24px;
}
.v2026-mac__loading,
.v2026-mac__error {
  margin: 0;
  padding: 24px 0;
  text-align: center;
  font-size: 14px;
  color: #667085;
}
.v2026-mac__error {
  color: #D92D20;
}

.v2026-mac__options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}
.v2026-mac__option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #EBEDF0;
  border-radius: 10px;
  background: #FFFFFF;
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: #D0D5DD;
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
  }
  &.is-selected {
    border-color: var(--v2026-mac-main);
    background: rgba(49, 193, 215, 0.06);
  }
}
.v2026-mac__radio {
  flex: none;
  width: 16px;
  height: 16px;
  border: 1px solid #D0D5DD;
  border-radius: 50%;
  background: #FFFFFF;
}
.v2026-mac__option.is-selected .v2026-mac__radio {
  border: 5px solid var(--v2026-mac-main);
}
.v2026-mac__option-label {
  font-size: 13px;
  line-height: 1.45;
  color: #1F2937;
}
.v2026-mac__option.is-selected .v2026-mac__option-label {
  font-weight: 600;
}
.v2026-mac__card {
  display: flex;
  flex-direction: column;
  border: 1px solid #EBEDF0;
  border-radius: 12px;
  background: #FFFFFF;
  text-align: left;
  overflow: hidden;
}
.v2026-mac__content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  padding: 24px 24px 20px;
}
.v2026-mac__card-title {
  font-size: 15px;
  font-weight: 700;
  color: #111111;
}
.v2026-mac__line {
  font-size: 13px;
  color: #333333;
  white-space: pre-line;
}
.v2026-mac__note {
  font-size: 12px;
  color: #888888;
}
.v2026-mac__foot {
  padding: 12px 24px 16px;
  border-top: 1px solid #EBEDF0;
}
.v2026-mac__cta {
  display: block;
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--v2026-mac-line-green);
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
}

.v2026-mac__actions {
  flex: none;
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid #EBEDF0;
  background: #FFFFFF;
}
.v2026-mac__send {
  min-width: 148px;
  height: 44px;
  padding: 0 24px;
  border: none;
  border-radius: 8px;
  background: var(--v2026-mac-main);
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    background: #E4E7EC;
    color: #667085;
    cursor: default;
  }
}
</style>
